/**
 * Event-Binding für das TravelMenu.
 * Verbindet DOM-Elemente mit State-Aktionen und Sync-Funktionen.
 */
import type { TravelMenuDOMResult } from './TravelMenuDOM';
import type { TravelMenuState } from './TravelMenuState';
import type { TravelConfig } from '../config/TravelConfig';
import type { WorldInfo } from '../config/WorldConfig';
import type { PathfindingResponse } from '../workers/pathfinding.types';
import { buildPathfindingRequest } from '../systems/TravelRequestBuilder';
import { TravelMode } from '../systems/TravelTypes';
import { syncStep1, syncSliders, syncSliderLabel, syncWarpInfo, syncModeCards, syncSendBtn, CARD_TO_TRAVEL_MODE } from './TravelMenuWizardSync';

export interface PathfindingContext {
  travelConfig: TravelConfig;
  worldInfo: WorldInfo;
  sunKeys: number[];
}

export interface TravelMenuCallbacks {
  switchTab: (id: 'active' | 'inactive' | 'hub') => void;
  goToStep: (step: 1 | 2 | 3) => void;
  canAdvance: () => boolean;
  currentStep: () => 1 | 2 | 3;
  updateNextBtn: () => void;
  minimizeOverlay: (side: 'start' | 'end') => void;
  fullSync: () => void;
  getPathfindingContext: () => PathfindingContext;
}

/** Bindet alle DOM-Events im TravelMenu. */
export function bindTravelMenuEvents(
  dom: TravelMenuDOMResult,
  state: TravelMenuState,
  cb: TravelMenuCallbacks,
): void {
  // Tab-Buttons
  dom.tabBtns.active.addEventListener('pointerdown', (e) => { e.stopPropagation(); cb.switchTab('active'); });
  dom.tabBtns.inactive.addEventListener('pointerdown', (e) => { e.stopPropagation(); cb.switchTab('inactive'); });
  dom.tabBtns.hub.addEventListener('pointerdown', (e) => { e.stopPropagation(); cb.switchTab('hub'); });

  // Wizard: Back / Next
  dom.backBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    const cur = cb.currentStep();
    if (cur > 1) cb.goToStep((cur - 1) as 1 | 2 | 3);
  });
  dom.nextBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    const cur = cb.currentStep();
    if (cb.canAdvance() && cur < 3) cb.goToStep((cur + 1) as 1 | 2 | 3);
  });

  // Step 1: Planet-Selects
  dom.startSelect.addEventListener('change', () => {
    const id = dom.startSelect.value;
    if (!id) { state.selectedStart = null; }
    else { const p = state.ownPlanets.find(x => x.id === id); if (p) state.selectStartPlanet(p); }
    syncStep1(dom, state);
    cb.updateNextBtn();
  });

  dom.endSelect.addEventListener('change', () => {
    const id = dom.endSelect.value;
    if (!id) { state.selectedEnd = null; }
    else {
      const p = state.ownPlanets.find(x => x.id === id);
      if (p) state.selectedEnd = { planetId: p.id, q: p.q, r: p.r, label: p.label, isPlanet: true, isSun: false };
    }
    syncStep1(dom, state);
    cb.updateNextBtn();
  });

  // Step 1: Karten-Buttons
  dom.mapStartBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); cb.minimizeOverlay('start'); });
  dom.mapEndBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); cb.minimizeOverlay('end'); });

  // Step 2: Ship-Sliders
  for (let i = 0; i < 8; i++) {
    dom.sliders[i].addEventListener('input', () => {
      state.shipSliders[i] = Number(dom.sliders[i].value);
      syncSliderLabel(dom, state, i);
      syncWarpInfo(dom, state);
      cb.updateNextBtn();
    });
  }

  dom.allBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation(); state.selectAllShips();
    syncSliders(dom, state); syncWarpInfo(dom, state); cb.updateNextBtn();
  });
  dom.noneBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation(); state.clearAllShips();
    syncSliders(dom, state); syncWarpInfo(dom, state); cb.updateNextBtn();
  });

  // Step 3: Mode-Cards
  for (let i = 0; i < dom.modeCards.length; i++) {
    dom.modeCards[i].addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (dom.modeCards[i].dataset.enabled !== '1') return;
      state.selectedMode = CARD_TO_TRAVEL_MODE[i];
      syncModeCards(dom, state);
      syncSendBtn(dom, state);
    });
  }

  // Step 3: Senden (mit Pathfinding)
  let _pathWorker: Worker | null = null;
  dom.sendBtn.addEventListener('pointerdown', async (e) => {
    e.stopPropagation();
    if (!state.canSend) return;

    dom.sendBtn.textContent = 'ROUTE...';
    dom.errorEl.textContent = '';

    const ctx = cb.getPathfindingContext();
    const start = state.selectedStart!;
    const end = state.selectedEnd!;
    const mode = state.selectedMode as TravelMode;

    // Pathfinding-Worker aufrufen
    try {
      if (!_pathWorker) {
        _pathWorker = new Worker(
          new URL('../workers/pathfinding.worker.ts', import.meta.url),
          { type: 'module' },
        );
      }
      const req = buildPathfindingRequest(
        { q: start.q, r: start.r }, { q: end.q, r: end.r },
        mode, ctx.travelConfig, ctx.worldInfo, ctx.sunKeys,
      );
      const resp = await new Promise<PathfindingResponse>((resolve) => {
        _pathWorker!.onmessage = (ev: MessageEvent<PathfindingResponse>) => resolve(ev.data);
        _pathWorker!.postMessage(req);
      });

      if (resp.type === 'path-error') {
        dom.sendBtn.textContent = 'FLOTTE SENDEN';
        dom.errorEl.textContent = resp.message || 'Keine Route gefunden';
        return;
      }

      const routeHexes = resp.route;
      const routeArray = routeHexes.map(h => [h.q, h.r]);
      const totalHops = Math.max(1, routeHexes.length - 1);

      dom.sendBtn.textContent = 'SENDE...';
      const ok = await state.sendFleet(totalHops, routeArray);

      dom.sendBtn.textContent = 'FLOTTE SENDEN';
      if (!ok) {
        dom.errorEl.textContent = state.errorMsg || 'Fehler beim Senden';
      } else {
        dom.errorEl.textContent = '';
        cb.goToStep(1);
        cb.switchTab('active');
        cb.fullSync();
      }
    } catch (err) {
      dom.sendBtn.textContent = 'FLOTTE SENDEN';
      dom.errorEl.textContent = 'Pathfinding fehlgeschlagen';
      console.error('[TravelMenu] Pathfinding error:', err);
    }
  });
}
